import type { FileExcelFilled } from '@ant-design/icons'
import type { ModalProps, TagProps } from 'antd'
import type { ButtonProps } from 'antd/es/button/button'
import type { ReactNode } from 'react'
import type { ReportTotals } from './components/check-register'

export interface BatchSelectionContextValue {
  selectedCheckRunIds: string[]
  selectedPaymentFileIds: string[]
  selectedEOBIds: string[]
  selectedClaimIds: string[]

  onSelectCheckRuns: (ids: string[]) => void
  onSelectPaymentFiles: (ids: string[]) => void
  onSelectEOBs: (ids: string[]) => void
  onSelectClaims: (ids: string[]) => void
}

export interface Group {
  id: string
  number: string
  name: string
  release_type: string | null
  release_day: number | null

  zelis_eligible: boolean | null
  activation_date: string | null
  checks_only: boolean | null
}

export interface GroupMetaEntry {
  key: string
  label?: React.ReactNode
  value: React.ReactNode
}

export interface Location {
  id: string
  name: string
  short_name: string
}

export interface CheckRun {
  id: string
  group: Group
  location: Location
  date: string
  payment_files: PaymentFile[]
  totals_checked: boolean
  type: 'scheduled' | 'on-demand'

  scheduled_files: ScheduledFile[]
  check_register_reports: CheckRegisterReport[]
  lcr_file: LCRFile | null
  nomi835_files: Nomi835File[]
  nomi_status_files: NomiStatusFile[]

  deleted_at: string | null
  next_release_date: string | null

  report_totals: ReportTotals

  status_ids: string[] | null
  file_names: string[] | null
  has_data: boolean | null
  flags: string[] | null
  amount: number | null
  voided_checks: VoidedCheck[] | null

  synced_at: string | null

  warning: string | null
  error_details?: string | null

  is_network: boolean
  temporal_workflow_id?: string | null
}

export type PartialCheckRun = Omit<
  CheckRun,
  'scheduled_filed' | 'check_register_reports' | 'lcr_file' | 'payment_files'
>

export interface VoidedCheck {
  number: string
  amount: number
}

export interface LCRFile {
  id: string
  total: number
  voided: number
  net: number
  original_filename: string
}

export interface Nomi835File {
  name: string
  id: string
  created_at: string
  path: string
}

export interface NomiStatusFileDetail {
  source_file_name: string
  status: string
  exception_type: string
  created_at: string
}

export interface NomiStatusFile {
  name: string
  id: string
  created_at: string
  path: string
  source_files_details?: NomiStatusFileDetail[]
  // Legacy fields for backward compatibility
  source?: string
  status?: string
  exception_type?: string
}

export interface ScheduledFile {
  id: string
  name: string
}

export interface CheckRegisterReport {
  id: string
  name: string
  type:
    | 'detail'
    | 'summary'
    | 'one-page'
    | 'winsupply'
    | 'network-summary'
    | 'network-detail'
    | 'allied-oms'
  cause: 'job' | 'manual'
  options: Record<string, unknown> | null
  version: string
  created_at: string

  totals: Record<string, Record<'payments' | 'voids', { count: number; amount: number }>> | null

  meta: {
    group: Group
  } | null

  set: {
    id: string
    created_at: string
  } | null

  user: {
    id: string
    email: string
    first_name: string
    last_name: string
  } | null
}

export interface EOB {
  id: string
  check_amount: number
  claims: Claim[]

  is_on_hold: boolean
  on_hold_reason: string | null
  is_flagged_stop_loss: boolean
  has_multiple_patients: boolean
  is_eligible: boolean
  has_ineligible_claims: boolean
  payment_method: string
  zelis_payment_method: string | null
  nomi_claims_status: ClaimStatus[]
  x937_check_id?: string | null
  x937_paid_date?: string | null
}

export interface PaymentFile {
  id: string
  date: string
  filename: string
  eobs_summary: {
    number: string
    total: number
    check_amount_sum: number
    is_on_hold_total: number
    is_flagged_stop_loss_total: number
    has_multiple_patients_total: number
  }
  status: PaymentFileStatus
  file_total: number
  ach: number
  vcc: number
  manual_check: number
  proc_total: number

  lcr_total: number
  lcr_voided: number
  lcr_net: number

  events: {
    sent_to_zelis: PaymentFileEvent[]
    received_from_zelis: PaymentFileEvent[]
  }

  eobs: EOB[]
  deleted_at: string | null
}

export interface PaymentFileStatus {
  id: string
  name: string
  is_long_term: boolean
  count: number
}

export interface PaymentFileEvent {
  id: string
  type: 'sent-to-zelis' | 'received-from-zelis'
  filename: string
  original_filename: string
  created_at: string
}

export interface ClaimStatusHistory {
  id: string
  nomi_claim_id: string
  eob: string
  status: string
  last_update_ts: string
  exception_type: string
  exception_reason_type: string
  source_file_name: string
  nomi_file_receipt_ts: string
  status_report_file_name: string
  created_at: string
}

export interface ClaimStatus {
  nomi_claim_id: string
  tpa_claim_id: string | null
  source_file_name: string | null
  nomi_file_receipt_ts: string | null
  last_update_ts: string | null
  status: string | null
  exception_type: string | null
  exception_reason_type: string | null
}

export interface Claim {
  id: string
  claim_number: string
  payee: Payee
  provider: Provider
  amount: number
  check_number: string
  is_network: boolean
  is_eligible: boolean
  has_multiple_patients: boolean
  patient_names?: string

  diagnostics: {
    record_type: string
    claim_type: string
    clservpc: string
    run_date: string
    eep_provider_id: string | null
    eealp_provider_tin: string | null
  }

  nomi_claims_status: ClaimStatus[]
}

export interface Payee {
  id: string
  name: string
}

export interface Provider {
  id: string
  name: string
}

export interface PaymentMethodConfig {
  label: string
  value: string
}

export interface State {
  searchQuery: string

  expandedRowIds: string[]

  selectedCheckRunIds: string[]
  selectedPaymentFileIds: string[]
  selectedEOBIds: string[]
  selectedClaimIds: string[]

  releaseSettingsModal: {
    isOpen: boolean
    isSubmitting: boolean
    error?: Error
  }

  deleteCheckRunsModal: {
    isOpen: boolean
    checkRunIds: string[]
    isSubmitting: boolean
    error?: Error
  }

  rerunCheckRunModal: {
    isOpen: boolean
    checkRunIds: string[]
  }

  dropoffModal: {
    isOpen: boolean
    checkRun?: {
      id: string
    }
  }

  filters: {
    statusIds: string[]
    locationIds: string[]

    date: {
      from?: string
      to?: string
    }

    scheduledReleaseDate: {
      from?: string
      to?: string
    }

    flags: string[]
    releaseType: string[]

    groupNumber?: string
    groupName?: string

    amount: string[]
  }

  pagination: {
    currentPage: number
    pageSize: number
  }

  refreshInterval?: number

  liveUpdatesEnabled: boolean
}

export type Action =
  | { type: 'releaseSettingsModal/open' }
  | { type: 'releaseSettingsModal/close' }
  | { type: 'releaseSettingsModal/submit' }
  | { type: 'releaseSettingsModal/submit/success' }
  | { type: 'releaseSettingsModal/submit/error'; payload: Error }
  | { type: 'deleteCheckRunsModal/open'; payload: string[] }
  | { type: 'deleteCheckRunsModal/close' }
  | { type: 'deleteCheckRunsModal/submit' }
  | { type: 'deleteCheckRunsModal/submit/success' }
  | { type: 'deleteCheckRunsModal/submit/error'; payload: Error }
  | { type: 'rerunCheckRunModal/open'; payload: string[] }
  | { type: 'rerunCheckRunModal/close' }
  | { type: 'rerunCheckRunModal/submit' }
  | { type: 'rerunCheckRunModal/submit/success' }
  | { type: 'rerunCheckRunModal/submit/error'; payload: Error }
  | { type: 'dropoffModal/open'; payload: { id: string } }
  | { type: 'dropoffModal/close' }
  | { type: 'dropoffModal/submit' }
  | { type: 'dropoffModal/submit/success' }
  | { type: 'dropoffModal/submit/error'; payload: Error }
  | { type: 'rows/expand'; payload: string[] }
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse'; payload: string }
  | { type: 'checkRunIds/select'; payload: string[] }
  | { type: 'paymentFileIds/select'; payload: string[] }
  | { type: 'eobIds/select'; payload: string[] }
  | { type: 'claimIds/select'; payload: string[] }
  | { type: 'pagination/currentPage/set'; payload: number }
  | { type: 'pagination/pageSize/set'; payload: number }
  | { type: 'filters/set'; payload: State['filters'] }
  | { type: 'filters/clear' }
  | { type: 'searchQuery/set'; payload: string }
  | { type: 'refreshInterval/set'; payload: number }
  | { type: 'refreshInterval/clear' }
  | { type: 'liveUpdatesEnabled/set'; payload: boolean }

export interface StatusGroup {
  name: string
  value: string
  className?: string
  statusIds: string[]
}
export interface HumanizeDurationOptions {
  short?: boolean
}

export interface VoidCheckRunMutationVariables {
  ids: string[]
}

export interface RestoreCheckRunMutationVariables {
  id: string
}

export interface EobMetaResponse {
  code: string
  message: string
  meta?: {
    startTime: string
    status: string
  }
}

export interface DeleteCheckRunsMutationVariables {
  ids: string[]
}

export interface RerunCheckRunModalState {
  targetPaymentFilesStatusId?: string
  resetTotalsChecked: boolean
  rebuildCheckRegisterReports: boolean
  dropOffToLin: boolean
  emailNotification: boolean
  reason: string
}

export const initialRerunCheckRunModalState: RerunCheckRunModalState = {
  targetPaymentFilesStatusId: '4',
  resetTotalsChecked: true,
  rebuildCheckRegisterReports: true,
  dropOffToLin: true,
  emailNotification: true,
  reason: ''
}

export interface RerunCheckRunModalProps extends ModalProps {
  checkRunIds: string[]
  onSuccess?: () => void
  onError?: (err: unknown) => void
  onCancel?: () => void
}

export interface DropoffModalProps extends ModalProps {
  checkRun?: {
    id: string
  }
  onSuccess?: () => void
  onError?: (err: unknown) => void
  onCancel?: () => void
}

export interface CheckRunExpandableRowState {
  expandedRowIds: string[]
}

export type CheckRunExpandableRowAction =
  | { type: 'rows/expand'; payload: string[] }
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse'; payload: string }

export interface CheckRunsResponse {
  data: PartialCheckRun[]
  count: number
  total: number
}

export interface Response {
  status: Array<PaymentFileStatus & { count: number }>
  location: Array<Location & { count: number }>
  releaseType: Array<{ release_type: string | null; release_day: number | null; count: number }>
}

export interface RebuildCheckRunMutationVariables {
  id: string
}

export interface RefreshCheckRunCacheMutationVariables {
  id: string
}

export interface MutationVariables extends Partial<RerunCheckRunModalState> {
  ids: string[]
}

export interface MutationVariablesDropOffModal {
  id: string
  emailNotification?: boolean
  reports: string[]
}

export type CheckRunExpandableRowProps = PartialCheckRun

export interface CheckRunGeneralInformationWrapperProps {
  checkRunId: string
  groupId: string
}

export enum PaymentMethodDisplayType {
  Auto = 'auto',
  LastReport = 'last-report',
  Claims = 'claims'
}

export interface BaseTimelineEvent {
  id: string
  type: string
  date: Date
  data: Record<string, unknown> | null
}

export interface CRReportTimelineEvent extends BaseTimelineEvent {
  type: 'cr-report'
  data: {
    id: string
    type: string
    name: string
    version: string
    cause: string

    options: Record<string, unknown> | null
    totals: CheckRegisterReport['totals']
    meta: CheckRegisterReport['meta']

    user?: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }
}

export interface Nomi835TimelineEvent extends BaseTimelineEvent {
  type: 'nomi835'
  data: {
    path: string
    name: string
  }
}

export interface NomiStatusFileTimelineEvent extends BaseTimelineEvent {
  type: 'nomi_status_file'
  data: {
    path: string
    name: string
    status: string
    source: string
    exceptionType?: string
  }
}

export interface LCRFileTimelineEvent extends BaseTimelineEvent {
  type: 'lcr-file'
  data: {
    original_filename: string
    source: PickupSource | null
    id: string
  }
}

export interface ScheduledFileTimelineEvent extends BaseTimelineEvent {
  type: 'scheduled-file'
  data: {
    host: string
    path: string
    name: string
  }
}

export interface PrepassFileTimelineEvent extends BaseTimelineEvent {
  type: 'sent-to-zelis'
  data: {
    filename: string
  }
}

export interface EpayFileTimelineEvent extends BaseTimelineEvent {
  type: 'received-from-zelis'
  data: {
    filename: string
  }
}

export interface PaymentFileTimelineEvent extends BaseTimelineEvent {
  type: 'payment-file'
  data: {
    filename: string
    source: PickupSource | null
  }
}

export interface NotificationTimelineEvent extends BaseTimelineEvent {
  type: 'notification'
  data: {
    channel: string
    name: string
    address: string
    subject: string
  }
}

export interface RerunTimelineEvent extends BaseTimelineEvent {
  type: 'rerun'
  data: {
    user: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }
}

export interface VoidTimelineEvent extends BaseTimelineEvent {
  type: 'void'
  data: {
    user: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }
}

export interface RestoreTimelineEvent extends BaseTimelineEvent {
  type: 'restore'
  data: {
    user: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }
}

export interface DropoffTimelineEvent extends BaseTimelineEvent {
  type: 'dropoff'
  data: {
    dropoff_type: string
    destination: string
    folder: string
    name: string
    report_type: string
    status: 'scheduled' | 'failed' | 'success'
    error: string
    scheduled_at: string | null
    lin_timedout?: boolean
    lin_code?: string
    lin_signal?: string
    lin_output?: string
    lin_error?: string
  }
}

export interface PaymentFileManualTimelineEvent extends BaseTimelineEvent {
  type: 'payment-file-manual'
  data: {
    id: string
    name: string
    type: string
    source: string
    user: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }
}

export interface LCRFileManualTimelineEvent extends BaseTimelineEvent {
  type: 'lcr-file-manual'
  data: {
    id: string
    name: string
    type: string
    source: string
    user: {
      id: string
      first_name: string
      last_name: string
      email: string
    }
  }
}

export interface SinglePaymentTypeWarningTimelineEvent extends BaseTimelineEvent {
  type: 'single-payment-type-warning'
  data: {
    filename: string
    paymentType?: string
  }
}

export interface SingleProviderWarningTimelineEvent extends BaseTimelineEvent {
  type: 'single-provider-warning'
  data: {
    filename: string
    provider?: string
  }
}

export type TimelineEvent =
  | CRReportTimelineEvent
  | LCRFileTimelineEvent
  | ScheduledFileTimelineEvent
  | PrepassFileTimelineEvent
  | EpayFileTimelineEvent
  | PaymentFileTimelineEvent
  | NotificationTimelineEvent
  | RerunTimelineEvent
  | VoidTimelineEvent
  | RestoreTimelineEvent
  | DropoffTimelineEvent
  | Nomi835TimelineEvent
  | NomiStatusFileTimelineEvent
  | PaymentFileManualTimelineEvent
  | LCRFileManualTimelineEvent
  | SinglePaymentTypeWarningTimelineEvent
  | SingleProviderWarningTimelineEvent

export interface TimelineProps {
  id: string
  onClose: () => void
}

export interface TimelineEventConfig {
  title: string | ((event: TimelineEvent) => string)
  color: string
  icon: typeof FileExcelFilled
}

export interface StatusConfig {
  name: string
  color?: TagProps['color']
}

export interface GroupSetting {
  label: string
  value: React.ReactNode
}

export interface Stat {
  label: string
  amount: number
  isEmpty: boolean
  tooltip?: React.ReactNode
}

export interface File {
  type: string
  name: React.ReactNode
  href: string
  priority?: number
  icon?: {
    component: React.ComponentType<{ className?: string }>
    className?: string
  }
  // Additional fields for nomi_status_file type
  sourceFilesDetails?: NomiStatusFileDetail[]
}

export interface FileGroup {
  name: string
  files: File[]
}

export interface TimelineGroup {
  key: string
  events: TimelineEvent[]
}

export interface PaymentFileExpandableRowState {
  onHoldModal: {
    form: {
      reason: string
    }

    isOpen: boolean
    eobIds: string[]
    isSubmitting: boolean
    error?: Error
  }

  expandedRowIds: string[]
}

export type PaymentFileExpandableRowAction =
  | { type: 'rows/expand'; payload: string[] }
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse'; payload: string }
  | { type: 'onHoldModal/open'; payload: string[] }
  | { type: 'onHoldModal/close' }
  | { type: 'onHoldModal/submit' }
  | { type: 'onHoldModal/submit/success' }
  | { type: 'onHoldModal/submit/error'; payload: Error }
  | { type: 'onHoldModal/form/reason/set'; payload: string }

export interface PaymentFileExpandableRowProps {
  checkRunId: string
  groupId: string
  paymentFileId: string
  paymentFileName: string
}

export interface EOBExpandableRowProps {
  checkRunId: string
  groupId: string
  paymentFileId: string
  eobNumber: string
}

export interface BatchSelectionPanelProps {
  colSpan?: number
  selectedItemsCount: number
  pluralization?: [string, string]
  children?: React.ReactNode
  onClearSelection?: () => void
}

export type BatchActionButtonProps = ButtonProps

export interface MiniDashboardProps {
  scheduledIsActive: boolean
  onScheduledClick: () => void

  builtIsActive: boolean
  onBuiltClick: () => void

  waitingIsActive: boolean
  onWaitingClick: () => void

  voidedIsActive: boolean
  onVoidedClick: () => void

  noPaymentFileIsActive: boolean
  onNoPaymentFileClick: () => void

  pendingIsActive: boolean
  onPendingClick: () => void

  failedIsActive: boolean
  onFailedClick: () => void

  nomiIsActive: boolean
  onNomiClick: () => void
}

export interface MetaTotalsResponse {
  today: {
    scheduled: number
    built: number
    waiting: number
    voided: number
  }
  total: {
    no_payment_file: number
    pending: number
    failed: number
  }
}

export interface Section {
  label: string
  stats: Entry[]
}

export interface Entry {
  label: React.ReactNode
  value: React.ReactNode
  tooltip?: string
}

export interface HumanReadableTimeProps {
  value: Date
}

export interface BaseRerunAction {
  key: string
  success: boolean
}

export interface SuccessRerunAction extends BaseRerunAction {
  success: true
}

export interface FailedRerunAction extends BaseRerunAction {
  success: false
  reason: string
}

export type RerunAction = SuccessRerunAction | FailedRerunAction

export interface RerunResponse {
  actions: RerunAction[]
}

export interface CardProps {
  title: string
  hasData?: boolean
  value?: string | number | null
  error?: {
    showError: boolean
    value?: number | null
  }

  details?: Array<{
    title?: string
    value?: string | number | null
    data: Array<{
      title: string
      value: string | number | undefined
      tooltip?: ReactNode
      forceNumber?: boolean
    }>
  }>
}

export interface TotalsProps {
  lcrTotal?: number
  luminxTotal?: number | null
  lcr_file: LCRFile | null
  payment_files: PaymentFile[]
  isLoading?: boolean
  hasData?: boolean
}

export enum PickupSource {
  S3 = 's3',
  SFTP = 'sftp'
}

export interface StatDiagnostics {
  key: string
  values: Array<string | null>
}
